// SwitchCompanyModal — the user menu's "Switch company", in place. Lists the
// companies this user belongs to (the same users/all-companies query the
// post-login picker uses) and enters the chosen one through the shared
// useSelectCompany flow, which is where the RTK Query cache reset lives — see
// that hook for why switching without it would re-serve the previous company's
// data.
//
// Search is client-side and undebounced ON PURPOSE: the endpoint returns every
// company at once, so there is no request to throttle and useDebouncedValue
// would only add latency to a local filter.
//
// The row avatars are initials, not logos: the list payload carries
// { companyCode, companyId, companyName, nameOnMidani } and no logo field, so
// the design's company mark has nothing to render from. Avatar already accepts
// `src` — when the backend adds a URL it is one prop on the element below.

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Avatar, Chip, Icon, Input, Modal } from '@/components/common';
import { useAppSelector } from '@/store/hooks';
import { useListCompaniesQuery } from '@/api/endpoints/companyApi';
import useSelectCompany from '@/hooks/useSelectCompany';
import toneOf from '@/utils/avatarTone';
import initialsOf from '@/utils/initials';

interface SwitchCompanyModalProps {
  open: boolean;
  onClose: () => void;
}

const SwitchCompanyModal = ({ open, onClose }: SwitchCompanyModalProps) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const currentId = useAppSelector((state) => state.auth.companyId);

  const {
    data: companies = [],
    isLoading,
    isError,
  } = useListCompaniesQuery(undefined, { skip: !open });
  const { select, selecting, pendingId } = useSelectCompany();

  useEffect(() => {
    if (open) setSearch('');
  }, [open]);

  const q = search.trim().toLowerCase();
  const shown = q ? companies.filter((c) => c.name.toLowerCase().includes(q)) : companies;

  return (
    <Modal
      open={open}
      onClose={onClose}
      className='switch-company'
      icon='building-2'
      title={t('header_switch_company')}
      subtitle={t('switch_company_subtitle', { count: companies.length })}
    >
      <div className='switch-company__search'>
        <Input
          iconStart='search'
          placeholder={t('switch_company_search_ph')}
          aria-label={t('switch_company_search_ph')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div className='switch-company__list'>
        {isLoading && <p className='switch-company__status'>{t('text_loading')}</p>}
        {!isLoading && isError && <p className='switch-company__status'>{t('error_generic')}</p>}
        {!isLoading && !isError && companies.length === 0 && (
          <p className='switch-company__status'>{t('text_no_companies')}</p>
        )}
        {!isLoading && !isError && companies.length > 0 && shown.length === 0 && (
          <p className='switch-company__status'>{t('switch_company_no_match')}</p>
        )}

        {shown.map((company) => {
          const isCurrent = company.id === currentId;
          return (
            // A button, not a styled div — the rows are the modal's only real
            // controls and have to take keyboard focus.
            <button
              key={company.id}
              type='button'
              className={`switch-company__row${isCurrent ? ' switch-company__row--current' : ''}`}
              // The current company is not a destination; everything else is
              // locked out only while a switch is in flight.
              disabled={isCurrent || selecting}
              onClick={() => select(company.id, onClose)}
            >
              <Avatar initials={initialsOf(company.name)} size={40} tone={toneOf(company.name)} />
              <span className='switch-company__name'>{company.name}</span>
              <span className='switch-company__trail'>
                {isCurrent ? (
                  <Chip variant='success' iconStart='check'>
                    {t('switch_company_current')}
                  </Chip>
                ) : (
                  <Icon
                    name={pendingId === company.id ? 'loader-circle' : 'arrow-right'}
                    size={18}
                    aria-hidden='true'
                  />
                )}
              </span>
            </button>
          );
        })}
      </div>

      {/* The design's band, not Modal's `footer` slot — that one right-aligns
          its children for an action row, and this is a note. */}
      <p className='switch-company__note'>
        <Icon name='info' size={16} aria-hidden='true' />
        {t('switch_company_note')}
      </p>
    </Modal>
  );
};

export default SwitchCompanyModal;
